import { Injectable } from '@angular/core';
import { Observable, from, switchMap, catchError, filter, map, tap } from 'rxjs';
import { HttpClient, HttpHeaders, HttpEventType, HttpResponse } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { handleErrorFunction } from '../../../shared/helpers/handleError';
import { EntraService } from '../../../service/entra.service';
import { FetchedPoValues } from '../../../models/poData/FetchedPoValues';
import { PackingSlipExtractionResult } from '../../../models/poData/PackingSlipExtractionResult';
import { GetReceivingLogsResponse } from '../../../models/logs/GetReceivingLogResponse';
import { AddReceivingLogResponse } from '../../../models/logs/AddReceivingLogResponse';
import { AddReceivingLogRequest } from '../../../models/logs/AddReceivingLogRequest';
import { ReceivingLogStatusResponse } from '../../../models/logs/ReceivingLogStatusResponse';
import { JobType } from '../../../models/logs/SubmitJob';
import { ReceivingLogRow } from '../../../models/logs/ReceivingLogRow';
import { ApiReceivingLogRow } from '../../../models/logs/ApiReceivingLogRow';
import { ReceivingLogSearch } from '../../../models/logs/ReceivingLogSearch';
import { ReceivingItemDetail } from '../../../models/logs/ReceivingItemDetail';
import { ReceivingItemRef } from '../../../models/logs/ReceivingItemRef';
import { ReceivingLogDetails } from '../../../models/logs/ReceivingLogDetails';
import { UpdateReceivingLogRequest } from '../../../models/logs/UpdateReceivingLogRequest';
import { UpdateReceivingLogResponse } from '../../../models/logs/UpdateReceivingLogResponse';

/**
 * Pure HTTP client for the receiving-log backend endpoints.
 *
 * Every method acquires a bearer token from EntraService, makes the request,
 * and returns an Observable. No state, no toasts, no loading flags — callers
 * (PoLookupService, LogSubmissionService, ReceivingLogStateService) own those concerns.
 * Errors are normalized through handleErrorFunction into
 * `{ message, details, originalError }` objects.
 */
@Injectable({
  providedIn: 'root',
})
export class ReceivingLogApiService {
  constructor(
    private _httpClient: HttpClient,
    private _entraService: EntraService
  ) {}

  /**
   * Acquires a token and runs the given request with Authorization headers,
   * piping errors through the centralized error handler.
   */
  private authorizedRequest<T>(
    request: (headers: HttpHeaders) => Observable<T>
  ): Observable<T> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const headers: HttpHeaders = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        });
        return request(headers).pipe(
          catchError((error) => handleErrorFunction(error))
        );
      })
    );
  }

  /** GET /V1/validate-po/{poNumber} — whether the PO exists. */
  public getPoValidation(poNumber: string): Observable<boolean> {
    return this.authorizedRequest((headers) =>
      this._httpClient.get<boolean>(
        `${environment.apiUrl}/V1/validate-po/${poNumber}`,
        { headers }
      )
    );
  }

  /** GET /V1/po-data/{poNumber} — job, contact, and location data for the PO. */
  public getPoData(poNumber: string): Observable<FetchedPoValues> {
    return this.authorizedRequest((headers) =>
      this._httpClient.get<FetchedPoValues>(
        `${environment.apiUrl}/V1/po-data/${poNumber}`,
        { headers }
      )
    );
  }

  /**
   * POST /V1/extract-po — extracts a PO number (and line items) from packing-slip
   * page images. Files are appended in order so page 1 is first; the browser sets
   * the multipart boundary (do not set Content-Type manually).
   */
  public extractPackingSlipPo(
    files: File[]
  ): Observable<PackingSlipExtractionResult> {
    return this.authorizedRequest((headers) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file, file.name));
      return this._httpClient.post<PackingSlipExtractionResult>(
        `${environment.apiUrl}/V1/extract-po`,
        formData,
        { headers }
      );
    });
  }

  /** POST /V1/all-receiving-logs — receiving logs matching the search criteria. */
  public getReceivingLogs(
    receivingLogSearch: ReceivingLogSearch
  ): Observable<GetReceivingLogsResponse> {
    return this.authorizedRequest((headers) =>
      this._httpClient.post<GetReceivingLogsResponse>(
        `${environment.apiUrl}/V1/all-receiving-logs`,
        receivingLogSearch,
        { headers }
      )
    );
  }

  /**
   * POST /V1/receiving-log — submits a new receiving log with optional photo files
   * as multipart form data (log JSON in the 'ReceivingLog' field, shipment proof
   * files in 'Photos'). Packing-slip pages go in a separate 'SlipPhotos' field
   * (named `packing-slip-{n}.{ext}`) so the backend tags them Kind='Slip' — this
   * is what lets the "AI got the PO wrong" feedback resolve its slip images by log
   * ID (the field name must match the backend's split exactly). Per-item damage
   * photos go in a separate 'DamagePhotos' field, each named
   * `damage-{receiptIndex}-{n}.{ext}` so the backend can attach it to the matching
   * receipt row. Each set is kept out of 'Photos' so their type is unambiguous.
   *
   * `onUploadProgress` reports bytes sent while the multipart body is still going
   * out. On a warehouse uplink this request spends nearly all of its time here —
   * a measured 226s request was 225s of inbound body and ~1s of server work — so
   * a caller that wants to distinguish "slow but moving" from "hung" has to watch
   * these callbacks rather than the total elapsed time. Callers that don't pass it
   * see exactly the same single-response stream as before.
   */
  public postReceivingLog(
    receivingLogRequest: AddReceivingLogRequest,
    photoFiles?: File[],
    damagePhotoFiles?: File[],
    slipPhotoFiles?: File[],
    onUploadProgress?: (loaded: number, total?: number) => void
  ): Observable<AddReceivingLogResponse> {
    return this.authorizedRequest((headers) => {
      const formData = new FormData();
      formData.append('ReceivingLog', JSON.stringify(receivingLogRequest));
      if (photoFiles && photoFiles.length > 0) {
        for (let i = 0; i < photoFiles.length; i++) {
          formData.append('Photos', photoFiles[i], photoFiles[i].name);
        }
      }
      if (slipPhotoFiles && slipPhotoFiles.length > 0) {
        for (let i = 0; i < slipPhotoFiles.length; i++) {
          formData.append('SlipPhotos', slipPhotoFiles[i], slipPhotoFiles[i].name);
        }
      }
      if (damagePhotoFiles && damagePhotoFiles.length > 0) {
        for (let i = 0; i < damagePhotoFiles.length; i++) {
          formData.append('DamagePhotos', damagePhotoFiles[i], damagePhotoFiles[i].name);
        }
      }
      return this._httpClient
        .post<AddReceivingLogResponse>(
          `${environment.apiUrl}/V1/receiving-log`,
          formData,
          { headers, observe: 'events', reportProgress: !!onUploadProgress }
        )
        .pipe(
          tap((event) => {
            if (onUploadProgress && event.type === HttpEventType.UploadProgress) {
              onUploadProgress(event.loaded, event.total);
            }
          }),
          // Collapse the event stream back to the one response the callers expect.
          filter(
            (event): event is HttpResponse<AddReceivingLogResponse> =>
              event.type === HttpEventType.Response
          ),
          map((event) => event.body as AddReceivingLogResponse)
        );
    });
  }

  /** PUT /V1/update-receiving-log — updates an existing receiving log. */
  public putReceivingLog(
    receivingLogRow: ReceivingLogRow
  ): Observable<AddReceivingLogResponse> {
    return this.authorizedRequest((headers) => {
      const apiReceivingLogRow = new ApiReceivingLogRow(receivingLogRow);
      return this._httpClient.put<AddReceivingLogResponse>(
        `${environment.apiUrl}/V1/update-receiving-log`,
        apiReceivingLogRow,
        { headers }
      );
    });
  }

  /** POST /V1/move-from-archive/{archiveRowId} — restores an archived log. */
  public moveReceivingLogFromArchive(
    archiveRowId: number
  ): Observable<{ Message: string; NewId: number }> {
    return this.authorizedRequest((headers) =>
      this._httpClient.post<{ Message: string; NewId: number }>(
        `${environment.apiUrl}/V1/move-from-archive/${archiveRowId}`,
        {},
        { headers }
      )
    );
  }

  /**
   * GET /V1/receiving-item-details/{itemGuid} — the full detail view for a
   * scanned receiving item: the item row, its parent log row, and the
   * receipt-ledger rows for that log, aggregated in one round-trip. Looked up
   * by the item's GUID (the QR URL's `payload` value); 404s (unknown GUID)
   * arrive through the normalized error shape.
   */
  public getReceivingItem(itemGuid: string): Observable<ReceivingItemDetail> {
    return this.authorizedRequest((headers) =>
      this._httpClient.get<ReceivingItemDetail>(
        `${environment.apiUrl}/V1/receiving-item-details/${encodeURIComponent(itemGuid)}`,
        { headers }
      )
    );
  }

  /**
   * GET /V1/receiving-log/{logId}/item-ids — the log's per-item rows
   * (MM.ReceivingItems), ordered by ItemIndex: each item's GUID plus the caption
   * facts its label prints. Same rows a fresh submit returns on `receivingItems`,
   * fetched lazily by logId so the log table doesn't have to enrich every row up
   * front.
   *
   * Empty array for legacy logs (no item rows) and for items keyed off an
   * archived log — the caller then omits `qr` and prints the no-QR label.
   *
   * ⚠️ The response used to be a flat `Guid[]`; it is now the full per-item
   * reference. Anything still treating it as an array of strings will break.
   */
  public getReceivingLogItems(logId: number): Observable<ReceivingItemRef[]> {
    return this.authorizedRequest((headers) =>
      this._httpClient.get<ReceivingItemRef[]>(
        `${environment.apiUrl}/V1/receiving-log/${logId}/item-ids`,
        { headers }
      )
    );
  }

  /**
   * GET /V1/receiving-log/{logId}/details — the log-edit screen's single load:
   * the log row, receipt-ledger rows (with ids), per-unit item rows (including
   * voided ones), the active-item-id list, the collapsed container list, the
   * derived quantity preview, PO completion status, and print history — one
   * round-trip in place of stitching together `item-ids`, `receiving-item-details`
   * and `po-data` (handoff-18 §1).
   *
   * `400` bad id, `404` no such log, `500` core read failed — those surface
   * through the normalized error shape like every other call here.
   */
  public getReceivingLogDetails(logId: number): Observable<ReceivingLogDetails> {
    return this.authorizedRequest((headers) =>
      this._httpClient.get<ReceivingLogDetails>(
        `${environment.apiUrl}/V1/receiving-log/${logId}/details`,
        { headers }
      )
    );
  }

  /**
   * PUT /V1/receiving-log/{logId} — the STRUCTURED edit of an already-submitted
   * log (handoff-18 §3). Send only what changed; every section of the body is
   * optional except `editedBy`, and a body that changes nothing is a legitimate
   * `200` that writes nothing.
   *
   * This is NOT `putReceivingLog` above. That one (`PUT /V1/update-receiving-log`)
   * types over the log row's free-text `items`/`quantity`, which are projections
   * of the child rows now. Use it only for a confirmed legacy log
   * (`hasStructuredRows: false`), whose quantity TEXT is the one thing this
   * endpoint cannot edit (§7). It no longer touches the GP note on any save — it
   * writes `MM.ReceivingLog` and nothing else — so `items` is safe to send
   * through it.
   *
   * Build the body with `buildLogEditRequest` rather than by hand: three of its
   * rules fail in ways a `200` cannot show you — a wholesale `receipts.updated`
   * row re-opens closed PO lines, an `expected.activeItemIds` built from the
   * GET's `items[]` is a permanent `409` loop, and a `poNumber` on `logRow` is a
   * flat `400`.
   *
   * Status codes, all of which arrive through the normalized error shape:
   * `400` nothing was written and the body is a PLAIN STRING naming what to fix;
   * `404` no such log (or it was deleted/archived between the GET and this call);
   * `409` a concurrent edit stopped this one and the body is a
   * `{ message, detail }` wrapper (NOT a bare details payload) whose `detail` is
   * byte-identical to the GET — reload and redecide, never replay, since the
   * request is not idempotent; `500` an unexpected failure, rolled back.
   */
  public putReceivingLogEdit(
    logId: number,
    request: UpdateReceivingLogRequest
  ): Observable<UpdateReceivingLogResponse> {
    return this.authorizedRequest((headers) =>
      this._httpClient.put<UpdateReceivingLogResponse>(
        `${environment.apiUrl}/V1/receiving-log/${logId}`,
        request,
        { headers }
      )
    );
  }

  /** DELETE /V1/delete-receiving-log/{logId} — deletes a receiving log. */
  public deleteReceivingLog(logId: number): Observable<{ Message: string }> {
    return this.authorizedRequest((headers) =>
      this._httpClient.delete<{ Message: string }>(
        `${environment.apiUrl}/V1/delete-receiving-log/${logId}`,
        { headers }
      )
    );
  }

  /**
   * GET /V1/receiving-log/{id}/status — the live async-submit state of a log,
   * derived from its background jobs. `outcome` is `Pending` while work is still
   * running, `Complete` when every job succeeded, `NeedsAttention` when a job is
   * permanently failed. A log with no jobs (synchronous-mode submit) returns
   * `Complete` with an empty list, so polling it is harmless.
   */
  public getReceivingLogStatus(id: number): Observable<ReceivingLogStatusResponse> {
    return this.authorizedRequest((headers) =>
      this._httpClient.get<ReceivingLogStatusResponse>(
        `${environment.apiUrl}/V1/receiving-log/${id}/status`,
        { headers }
      )
    );
  }

  /**
   * POST /V1/receiving-log/{logId}/retry — re-enqueues the named (or all currently
   * failed) background jobs; it does not run the work inline. The worker re-uploads a
   * failed photo job from the backend's own durably-staged bytes, so no files are sent.
   * Returns the freshly-reset status (the retried jobs now `Pending`).
   *
   * `force` re-runs the named jobs even if they already Succeeded — used to re-send a
   * notification/expediting email that already went out (without photos) so it goes out
   * again now that the photos have uploaded. The backend stamps a header note on the
   * re-sent email. Without `force`, already-succeeded jobs are left untouched.
   *
   * @param jobTypes Job types to re-enqueue; omit/empty to re-enqueue all failed jobs.
   * @param force When true, re-run the named jobs even if they already succeeded.
   */
  public retryReceivingLogJobs(
    logId: number,
    jobTypes?: JobType[],
    force: boolean = false
  ): Observable<ReceivingLogStatusResponse> {
    return this.authorizedRequest((headers) => {
      const formData = new FormData();
      if (jobTypes && jobTypes.length > 0) {
        formData.append('jobTypes', JSON.stringify(jobTypes));
      }
      if (force) {
        formData.append('force', 'true');
      }
      return this._httpClient.post<ReceivingLogStatusResponse>(
        `${environment.apiUrl}/V1/receiving-log/${logId}/retry`,
        formData,
        { headers }
      );
    });
  }
}
